import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  SimpleGrid,
  Spinner,
  Switch,
  Text,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import { Select as RSelect } from 'chakra-react-select';
import { Users } from 'phosphor-react';

import { GlassCard } from '../../../../../../components/glass';
import {
  useContaCilEmpresaContexto,
  useSalvarEmpresaConfig,
} from '../../../../../../hooks/useContaCilEmpresas';
import { IPocOption, useContaCilPocs } from '../../../../../../hooks/useContaCilLancamentos';
import { extractApiErrorMessage } from '../../../../../../utils/ErrorUtils';
import { fmtDoc } from '../../conciliar/shared';
import { Callout } from '../../plano-contas/ui';
import { propsMenuSelect } from '../../../../../../utils/selectMenu';

interface PocSelectOption {
  value: string;
  label: string;
}

export function AbaContextoIA({ empresaId }: { empresaId: number }) {
  const toast = useToast();
  const { data: ctx, isLoading } = useContaCilEmpresaContexto(empresaId);
  const { data: pocs = [] } = useContaCilPocs(empresaId);
  const salvar = useSalvarEmpresaConfig();

  const [poc, setPoc] = useState<string | null>(null);
  const [obs, setObs] = useState('');
  const [usarSocios, setUsarSocios] = useState(false);

  // Popula o form APENAS no primeiro carregamento ou quando trocar de
  // empresa. Antes o useEffect tinha [ctx] como dep — `ctx` eh objeto novo
  // a cada refetch (refetchOnWindowFocus default = true), entao bastava o
  // usuario tirar foco da janela (ir ler um e-mail) pra o background-refetch
  // criar novo ctx, o efeito rodar e SOBRESCREVER `obs` em digitacao.
  //
  // Deps primitivas extraidas via JSON.stringify do contexto_ia (objeto
  // pequeno, custo desprezivel) + poc_transitoria_codigo: so dispara
  // quando os VALORES no servidor mudam, nao a cada refetch.
  const pocServidor = ctx?.config.poc_transitoria_codigo ?? null;
  const contextoIaSerializado = JSON.stringify(ctx?.config.contexto_ia ?? {});
  useEffect(() => {
    if (!ctx) return;
    setPoc(pocServidor);
    const ia = ctx.config.contexto_ia ?? {};
    setObs(typeof ia.observacoes === 'string' ? ia.observacoes : '');
    setUsarSocios(Boolean(ia.usar_socios_fallback));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaId, pocServidor, contextoIaSerializado]);

  const pocOptions = useMemo<PocSelectOption[]>(
    () => pocs.map((p: IPocOption) => ({ value: p.codigo, label: `${p.codigo} · ${p.nome}` })),
    [pocs],
  );
  const pocValue = useMemo(() => pocOptions.find(o => o.value === poc) ?? null, [pocOptions, poc]);

  const onSalvar = async () => {
    try {
      await salvar.mutateAsync({
        empresaId,
        payload: {
          poc_transitoria_codigo: poc,
          contexto_ia: { observacoes: obs.trim() || null, usar_socios_fallback: usarSocios },
        },
      });
      toast({ title: 'Contexto salvo.', status: 'success' });
    } catch (err) {
      toast({ title: extractApiErrorMessage(err, 'Falha ao salvar.'), status: 'error' });
    }
  };

  if (isLoading) {
    return (
      <Flex justify="center" py={10}>
        <Spinner color="#0EA5E9" thickness="2px" />
      </Flex>
    );
  }

  const socios = ctx?.socios ?? [];

  return (
    <Box>
      {/* Bloco A — Sócios */}
      <GlassCard intensity="medium" p={5} mb={4}>
        <Flex align="center" gap={2} mb={3}>
          <Box color="#0EA5E9">
            <Users size={18} weight="duotone" />
          </Box>
          <Text fontSize="14px" fontWeight="700" color="#0F172A">
            Sócios
          </Text>
          <Text fontSize="11px" color="#94A3B8">
            a IA usa para detectar adiantamento/pró-labore
          </Text>
        </Flex>
        {socios.length === 0 ? (
          <Text fontSize="13px" color="#94A3B8">
            {ctx?.tem_plano
              ? 'Nenhum sócio cadastrado para esta empresa.'
              : 'Empresa sem plano vinculado — sócios indisponíveis.'}
          </Text>
        ) : (
          <Flex gap={2} wrap="wrap">
            {socios.map((s, i) => (
              <Flex
                key={`${s.cpf_cnpj ?? s.nome ?? i}`}
                align="center"
                gap={2}
                px={3}
                py={2}
                borderRadius="full"
                borderWidth="1px"
                borderColor="rgba(15,23,42,0.06)"
                bg="rgba(255,255,255,0.6)"
              >
                <Text fontSize="12px" fontWeight="600" color="#0F172A">
                  {s.nome || '—'}
                </Text>
                {s.cpf_cnpj && (
                  <Text fontSize="11px" color="#94A3B8">
                    {fmtDoc(s.cpf_cnpj)}
                  </Text>
                )}
                {s.participacao != null && (
                  <Badge colorScheme="cyan" borderRadius="full" fontSize="10px">
                    {s.participacao.toFixed(s.participacao % 1 === 0 ? 0 : 2)}%
                  </Badge>
                )}
                {s.cargo && (
                  <Text fontSize="10px" color="#64748B">
                    {s.cargo}
                  </Text>
                )}
              </Flex>
            ))}
          </Flex>
        )}
      </GlassCard>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
        {/* Bloco B — Conta transitória */}
        <GlassCard intensity="medium" p={5}>
          <Text fontSize="14px" fontWeight="700" color="#0F172A" mb={1}>
            Conta transitória (fallback)
          </Text>
          <Callout tone="info" titulo="Conceito">
            Quando a IA não identifica a contrapartida, o lançamento cai nesta conta para revisão
            manual — em vez de ficar sem destino.
          </Callout>
          <Box mt={3}>
            <RSelect<PocSelectOption>
              size="sm"
              placeholder={ctx?.tem_plano ? 'Escolha a conta transitória...' : 'Empresa sem plano'}
              isDisabled={!ctx?.tem_plano}
              options={pocOptions}
              value={pocValue}
              onChange={opt => setPoc(opt ? opt.value : null)}
              isClearable
              menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
              {...propsMenuSelect}
              styles={{ menuPortal: base => ({ ...base, zIndex: 2000 }) }}
              chakraStyles={{ control: provided => ({ ...provided, borderRadius: 'lg' }) }}
            />
          </Box>
        </GlassCard>

        {/* Bloco C — Flags + observações */}
        <GlassCard intensity="medium" p={5}>
          <Text fontSize="14px" fontWeight="700" color="#0F172A" mb={3}>
            Preferências de contexto
          </Text>
          <FormControl display="flex" alignItems="center" justifyContent="space-between" mb={4}>
            <FormLabel htmlFor="usar-socios" mb={0} fontSize="13px" color="#334155">
              Tratar contraparte sócio como adiantamento
            </FormLabel>
            <Switch
              id="usar-socios"
              colorScheme="cyan"
              isChecked={usarSocios}
              onChange={e => setUsarSocios(e.target.checked)}
            />
          </FormControl>
          <FormControl>
            <FormLabel fontSize="12px" color="#64748B" mb={1}>
              Observações para a IA
            </FormLabel>
            <Textarea
              size="sm"
              rows={3}
              borderRadius="lg"
              placeholder="Ex: contraparte X sempre vai para a conta Y; empresa tem filial Z..."
              value={obs}
              onChange={e => setObs(e.target.value)}
            />
          </FormControl>
        </GlassCard>
      </SimpleGrid>

      <Flex justify="flex-end" mt={4}>
        <Button
          borderRadius="full"
          bg="#0EA5E9"
          color="white"
          fontWeight="600"
          _hover={{ bg: '#0284C7' }}
          isLoading={salvar.isPending}
          onClick={onSalvar}
        >
          Salvar contexto
        </Button>
      </Flex>
    </Box>
  );
}
